import { Typography, Box } from "@material-ui/core"
import { COLOR_GRAY, STATUS_COLORS } from "../../../constants"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"
import { useTranslations } from "./useTranslations"
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Bar } from "react-chartjs-2"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { getOrCreateTooltip } from "../ProjectDashboard/util"

type CompletedProjectsScheduleGraphProps = {
  projectsDashboard: ProjectsDashboardViewModel
}

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, ChartDataLabels)

const useCompletedProjectsScheduleGraph = ({ projectsDashboard }: CompletedProjectsScheduleGraphProps) => {
  const {
    totalCompletedProjects,
    numberOfCompletedOnTimeProjects,
    numberOfCompletedLateProjects,
    numberOfCompletedOtherProjects,
    completedOnTimeProjects,
    completedLateProjects,
    completedOtherProjects,
    percentageOfCompletedOnTimeProjects,
    percentageOfCompletedLateProjects,
    percentageOfCompletedOtherProjects,
  } = projectsDashboard
  const translations = useTranslations({
    onTimeProjectCount: numberOfCompletedOnTimeProjects,
    lateProjectCount: numberOfCompletedLateProjects,
    noScheduleProjectCount: numberOfCompletedOtherProjects,
  })
  const isSmallScreen = useIsSmallScreen("xs")

  const displayChart = totalCompletedProjects > 0

  const onTimeColor = STATUS_COLORS.COMPLETED["1"]
  const lateColor = STATUS_COLORS.REJECTED["2"]
  const otherColor = STATUS_COLORS.NOT_STARTED["1"]

  const onTimeLabel = translations.onTimeProjectsLabel
  const lateLabel = translations.lateProjectsLabel
  const otherLabel = translations.noScheduleProjectsLabel

  const labels = [
    {
      label: onTimeLabel,
      boxColor: onTimeColor,
    },
    {
      label: lateLabel,
      boxColor: lateColor,
    },
    {
      label: otherLabel,
      boxColor: otherColor,
    },
  ]

  const data = [
    {
      value: numberOfCompletedOnTimeProjects,
      percentage: percentageOfCompletedOnTimeProjects.toFixed(1),
      tooltipContent: completedOnTimeProjects,
      backgroundColor: onTimeColor,
    },
    {
      value: numberOfCompletedLateProjects,
      percentage: percentageOfCompletedLateProjects.toFixed(1),
      tooltipContent: completedLateProjects,
      backgroundColor: lateColor,
    },
    {
      value: numberOfCompletedOtherProjects,
      percentage: percentageOfCompletedOtherProjects.toFixed(1),
      tooltipContent: completedOtherProjects,
      backgroundColor: otherColor,
    },
  ]

  const chartData = {
    labels: labels.map((l) => l.label),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map(() => "rgba(0,0,0,0)"),
        hoverBackgroundColor: data.map(() => "rgba(0,0,0,0)"),
        datalabels: {
          color: COLOR_GRAY,
          font: {
            size: isSmallScreen ? 12 : 16,
          },
          offset: 20,
          anchor: "start" as const,
          align: "end" as const,
          formatter: (_value: number, context: any) => {
            const label = context.chart.data.labels[context.dataIndex]
            return label
          },
        },
      },
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => d.backgroundColor),
        datalabels: {
          color: "#000",
          formatter: () => "",
        },
      },
    ],
  }

  const options = {
    indexAxis: "y" as const,
    responsive: true,

    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: (context: any) => {
          const { chart, tooltip } = context
          const tooltipEl = getOrCreateTooltip(chart)

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          if (tooltip.body) {
            const idx = tooltip.dataPoints[0].dataIndex
            const $data = data[idx]
            const projects = $data.tooltipContent || []
            if (projects.length === 0) {
              tooltipEl.style.opacity = "0"
              return
            }
            let innerHtml = ""
            projects.forEach((project: string) => {
              innerHtml += `<div>• ${project}</div>`
            })
            tooltipEl.innerHTML = innerHtml

            const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

            // Display, position, and set styles for font
            tooltipEl.style.opacity = 1
            tooltipEl.style.left = positionX + 20 + "px"
            tooltipEl.style.top = positionY + tooltip.caretY + "px"
          }
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: totalCompletedProjects + 2,
        title: { display: !isSmallScreen, text: translations.numberOfProjectsLabel },
      },
      y: {
        title: { display: false },
        ticks: { display: false },
      },
    },
  }

  return {
    displayChart,
    translations,
    options,
    chartData,
  }
}

const CompletedProjectsScheduleGraph = (props: CompletedProjectsScheduleGraphProps) => {
  const { displayChart, options, chartData, translations } = useCompletedProjectsScheduleGraph(props)

  if (!displayChart) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }

  return (
    <Box mt={3}>
      <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
    </Box>
  )
}

export default CompletedProjectsScheduleGraph
